import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMemberApp } from '../AppProvider';
import { memberApi } from '../api';
import { memberPath } from '@/config/hosts';

export default function CommunityEvents() {
  const { user } = useMemberApp();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const list = await memberApi.communityEvents();
      setEvents(list);
    } catch (err) {
      setError('Failed to load expert webinar lists.');
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      const result = await memberApi.toggleCommunityEventRSVP(eventId);
      setEvents((prev) =>
        prev.map((ev) => {
          if (ev.id === eventId) {
            return {
              ...ev,
              has_rsvp: result.has_rsvp,
              rsvps: result.has_rsvp ? [...ev.rsvps, user?.id] : ev.rsvps.filter((id) => id !== user?.id),
            };
          }
          return ev;
        })
      );
    } catch (err) {
      console.error('Failed to toggle RSVP', err);
    }
  };

  if (loading) {
    return (
      <div className="by-stage">
        <div className="by-empty" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="by-thinking" aria-label="Loading"><span /><span /><span /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="by-stage max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate(memberPath('/community'))}
        className="text-xs font-bold text-indigo-600 hover:underline flex items-center gap-1"
      >
        ← Back to Community Feed
      </button>

      <header className="space-y-1">
        <h1 className="text-xl font-extrabold text-slate-900">Expert Webinars & Events</h1>
        <p className="text-xs text-slate-500 leading-relaxed">
          Participate in live interactive seminars, Q&A blocks with certified clinical specialists, and wellness briefings in India.
        </p>
      </header>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-900 text-xs rounded-xl font-medium">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {events.length === 0 ? (
          <div className="md:col-span-2 text-center py-12 bg-white rounded-2xl border border-slate-100 text-slate-400 text-sm">
            No upcoming seminars available. Check back soon!
          </div>
        ) : (
          events.map((ev) => (
            <div key={ev.id} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between space-y-4">
              <div className="space-y-3">
                <span className="bg-indigo-50 text-indigo-700 text-[10px] font-extrabold px-2.5 py-1 rounded-full border border-indigo-100">
                  Onco-Nutrition & Science
                </span>
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900 leading-snug">{ev.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed mt-1.5">{ev.description}</p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-50 space-y-3">
                <div className="text-xs text-slate-700 font-medium space-y-1">
                  <div>🎙️ <b>{ev.speaker_name}</b></div>
                  <div className="text-slate-400 text-[11px]">{ev.speaker_title}</div>
                  <div className="pt-1.5 text-slate-600 font-semibold">⏰ {new Date(ev.date_time).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                </div>

                <div className="flex gap-2">
                  {ev.has_rsvp ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleRSVP(ev.id)}
                        className="flex-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold py-2.5 rounded-xl border border-emerald-200 transition"
                      >
                        ✓ Registered
                      </button>
                      <a
                        href={ev.zoom_link}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2.5 px-4 rounded-xl flex items-center justify-center transition"
                      >
                        Join Zoom
                      </a>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleRSVP(ev.id)}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2.5 rounded-xl transition"
                    >
                      RSVP & Get Link
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
